import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Snackbar, Typography,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import AccessTimeFilledRoundedIcon from '@mui/icons-material/AccessTimeFilledRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { gamesApi } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import './LuckyDrawScreen.css';

const formatDuration = (totalSeconds) => {
  const seconds = Math.max(0, totalSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

const getDurationLeft = (room) => {
  if (room.drawEndsAt) {
    return Math.max(0, Math.floor((new Date(room.drawEndsAt).getTime() - Date.now()) / 1000));
  }
  return room.durationLeft ?? 0;
};

const rankLabel = (rank) => {
  if (rank === 1) return '1st';
  if (rank === 2) return '2nd';
  if (rank === 3) return '3rd';
  return `${rank}th`;
};

function LuckyDrawScreen({ user, onBack, onWalletUpdate }) {
  const [rooms, setRooms] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setTick] = useState(0);

  const loadRooms = useCallback(() => {
    gamesApi.luckyDrawRooms().then((d) => {
      setRooms(d.rooms ?? []);
      setRecentResults(d.recentResults ?? []);
    }).catch(() => {});
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
    } catch {
      /* ignore */
    }
  }, [onWalletUpdate]);

  useEffect(() => {
    loadRooms();
    const refreshTimer = setInterval(loadRooms, 15000);
    return () => clearInterval(refreshTimer);
  }, [loadRooms]);

  useEffect(() => {
    const countdownTimer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(countdownTimer);
  }, []);

  const joinDraw = async (roomId) => {
    setLoading(true);
    try {
      await gamesApi.luckyDrawJoin(roomId);
      setToast('Joined the draw! Good luck 🎉');
      await refreshUser();
      loadRooms();
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const slotsWithPrizes = useMemo(
    () =>
      rooms.map((room) => {
        const targetPool = room.totalPrize ?? room.entryFee * room.maxParticipants;
        const breakdown = room.prizeBreakdown ?? {
          distributable: targetPool * 0.93,
          shares: [{ rank: 1, prize: targetPool * 0.93 }],
        };
        return {
          ...room,
          targetPool,
          poolAmount: room.prizePool,
          fee: room.entryFee,
          topPrize: breakdown.shares?.[0]?.prize ?? breakdown.distributable,
          prizeShares: breakdown.shares ?? [],
        };
      }),
    [rooms],
  );

  return (
    <Box className="lucky-root">
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />

      <Box className="lucky-header">
        <IconButton size="small" onClick={onBack} className="lucky-back-btn">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Box className="lucky-header__center">
          <EmojiEventsRoundedIcon sx={{ fontSize: 22, color: '#e9d5ff' }} />
          <Typography className="lucky-title">Lucky Draw</Typography>
        </Box>
        <Chip
          size="small"
          label={`₹${walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`}
          className="lucky-wallet-chip"
        />
      </Box>

      <Box className="lucky-scroll">
        <Box className="lucky-hero">
          <Typography className="lucky-hero__tag">JACKPOT ROOMS</Typography>
          <Typography className="lucky-hero__text">
            Join a contest — 7% platform fee, winners split the rest by rank (1st gets the most).
          </Typography>
          <Box className="lucky-live-pill">
            <span className="lucky-live-dot" />
            {slotsWithPrizes.length} room{slotsWithPrizes.length !== 1 ? 's' : ''} live
          </Box>
        </Box>

        {recentResults.length > 0 && (
          <Box className="lucky-winners-section">
            <Typography className="lucky-winners-heading">
              <WorkspacePremiumRoundedIcon sx={{ fontSize: 18 }} />
              Recent winners
            </Typography>
            {recentResults.map((result) => (
              <Box
                key={result.id}
                className={`lucky-winner-card ${result.winners?.some((w) => w.isMe) ? 'lucky-winner-card--me' : ''}`}
              >
                <Box className="lucky-winner-card__top">
                  <Typography className="lucky-winner-card__title">{result.title}</Typography>
                  <Typography className="lucky-winner-card__expires">
                    {formatDuration(result.expiresIn)}
                  </Typography>
                </Box>
                {(result.winners?.length > 1 ? result.winners : [result.winner]).filter(Boolean).map((w) => (
                  <Typography key={`${result.id}-${w.rank}`} className="lucky-winner-card__line">
                    <strong>{rankLabel(w.rank)} — {w.name}</strong>
                    {' won '}
                    <span className="lucky-winner-card__amount">
                      ₹{(w.prize ?? 0).toLocaleString('en-IN')}
                    </span>
                  </Typography>
                ))}
                <Typography className="lucky-winner-card__meta">
                  {result.winnerCount ?? 1} winner{(result.winnerCount ?? 1) > 1 ? 's' : ''} · {result.participantCount} players · pool ₹{result.prizePool?.toLocaleString('en-IN')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {slotsWithPrizes.length === 0 ? (
          <Box className="lucky-empty">
            <EmojiEventsRoundedIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            <Typography>No active draws right now — check back soon</Typography>
          </Box>
        ) : (
          slotsWithPrizes.map((slot, index) => {
            const durationLeft = getDurationLeft(slot);
            const alreadyJoined = slot.joinedByMe;
            const isFull = slot.participantCount >= slot.maxParticipants;
            const joinDisabled =
              loading || isFull || alreadyJoined || walletBalance < slot.fee || durationLeft <= 0;
            const fillPercent = Math.min(100, (slot.participantCount / slot.maxParticipants) * 100);

            return (
              <Box key={slot.id} className={`lucky-card lucky-card--${index % 3}`}>
                <Box className="lucky-card__glow" />
                <Box className="lucky-card__content">
                  <Box className="lucky-card-top">
                    <Box>
                      <Typography className="slot-title">{slot.title}</Typography>
                      <Typography className="slot-status">
                        {slot.status === 'FULL' ? 'Room full — drawing soon' : 'Open for entries'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      icon={<AccessTimeFilledRoundedIcon sx={{ fontSize: '14px !important' }} />}
                      label={formatDuration(durationLeft)}
                      className="lucky-timer-chip"
                    />
                  </Box>

                  <Box className="lucky-progress-wrap">
                    <Box className="lucky-progress-bar" style={{ width: `${fillPercent}%` }} />
                  </Box>
                  <Typography className="lucky-progress-label">
                    <PeopleAltRoundedIcon sx={{ fontSize: 14 }} />
                    {slot.participantCount} / {slot.maxParticipants} players joined
                  </Typography>

                  <Box className="lucky-metrics">
                    <Box className="metric-box">
                      <Typography className="metric-label">Entry</Typography>
                      <Typography className="metric-value">₹{slot.fee}</Typography>
                    </Box>
                    <Box className="metric-box metric-box--highlight">
                      <Typography className="metric-label">Prize pool</Typography>
                      <Typography className="metric-value metric-value--gold">
                        ₹{slot.targetPool.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </Typography>
                      {slot.poolAmount > 0 && slot.poolAmount < slot.targetPool ? (
                        <Typography className="metric-label" sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                          ₹{slot.poolAmount.toLocaleString('en-IN')} collected
                        </Typography>
                      ) : null}
                    </Box>
                    <Box className="metric-box">
                      <Typography className="metric-label">Winners</Typography>
                      <Typography className="metric-value">{slot.winnerCount ?? 1}</Typography>
                    </Box>
                  </Box>

                  <Box className="lucky-prize-split">
                    <Typography className="prize-title">
                      {slot.winnerCount === 1
                        ? `1 winner gets 90% (₹${slot.topPrize?.toLocaleString('en-IN', { maximumFractionDigits: 0 })})`
                        : `${slot.winnerCount} winners — ranked payouts`}
                    </Typography>
                    {slot.prizeShares?.length > 1 && (
                      <Box className="lucky-rank-chips">
                        {slot.prizeShares.map((share) => (
                          <span key={share.rank} className="lucky-rank-chip">
                            {rankLabel(share.rank)} ₹{share.prize?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </span>
                        ))}
                      </Box>
                    )}
                    <Typography className="lucky-fee-note">7% platform commission on total pool</Typography>
                  </Box>

                  {slot.participants?.length > 0 && (
                    <Box className="lucky-participants">
                      <Typography className="prize-title">In this room</Typography>
                      <Box className="participant-chips">
                        {slot.participants.slice(0, 8).map((p) => (
                          <span key={p.id} className={`participant-chip ${p.name === 'You' ? 'you' : ''}`}>
                            {p.name}
                          </span>
                        ))}
                        {slot.participants.length > 8 && (
                          <span className="participant-chip">+{slot.participants.length - 8}</span>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Button
                    fullWidth
                    className={`lucky-join-btn ${alreadyJoined ? 'joined' : ''}`}
                    onClick={() => joinDraw(slot.id)}
                    disabled={joinDisabled}
                  >
                    {alreadyJoined
                      ? '✓ You\'re in — good luck!'
                      : isFull
                        ? 'Room full'
                        : durationLeft <= 0
                          ? 'Draw ended'
                          : `Join for ₹${slot.fee}`}
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default LuckyDrawScreen;
